import { calculateServiceTotals } from "../../../contexts/BudgetContext/calc-methods";
import {
  type DefaultTax,
  type ServiceValues,
  serviceValues,
} from "../../../types/budget/service-item";
import type { DraftItem, DraftState, TaxKey, TaxValue } from "./draft";
import { FORNECEDOR_A_DEFINIR_ID, parseNumber } from "./draft";

const TAX_FIELDS: Record<TaxKey, keyof ServiceValues["taxData"]> = {
  ts: "serviceTax",
  iss: "iss",
  imp: "tribute",
  tmm: "fee",
  mk: "markup",
  imk: "supplier_tribute",
  com: "commission",
};

const SHARED_TAX_KEYS: TaxKey[] = ["ts", "iss", "imp", "tmm"];
const ITEM_TAX_KEYS: TaxKey[] = ["mk", "imk", "com"];

function cloneServiceValues(): ServiceValues {
  return JSON.parse(JSON.stringify(serviceValues)) as ServiceValues;
}

function isSharedItem(item: Pick<DraftItem, "cat" | "forma">): boolean {
  return Boolean(item.cat) && item.forma !== "SF";
}

function paramKey(item: Pick<DraftItem, "forma" | "forn" | "cat">): string {
  return `${item.forma || "SF"}|${item.forn || FORNECEDOR_A_DEFINIR_ID}|${
    item.cat || ""
  }`;
}

function effectiveTax(
  draft: DraftState,
  item: DraftItem,
  key: TaxKey
): TaxValue | undefined {
  if (SHARED_TAX_KEYS.includes(key) && isSharedItem(item)) {
    return draft.params[paramKey(item)]?.[key];
  }
  return item.tx[key];
}

function toBudgetTax(
  current: DefaultTax,
  tax: TaxValue | undefined
): DefaultTax {
  return {
    ...current,
    typeTax: tax?.mode === "R$" ? "FIX_VALUE" : "PERCENTAGE",
    value: tax?.v ? parseNumber(tax.v) : 0,
    currencyValue: 0,
    valueBRL: 0,
  };
}

function hasValue(tax: TaxValue | undefined): boolean {
  return Boolean(tax?.v);
}

function numberOfServices(draft: DraftState, item: DraftItem): number {
  const key = paramKey(item);
  return draft.items.filter((row) => {
    if (!isSharedItem(row) || paramKey(row) !== key) return false;
    if (!parseNumber(row.qtd) || (!parseNumber(row.dias) && row.dias !== "")) {
      return false;
    }
    return !hasValue(row.tx.mk) && !hasValue(row.tx.com);
  }).length;
}

/**
 * Produção adapts its draft row to the canonical Budget line calculator.
 * The result uses BRL because Produção has no exchange input.
 */
export function calculateProductionService(
  draft: DraftState,
  item: DraftItem
): ServiceValues | null {
  if (item.forma === "SF") return null;

  const values = cloneServiceValues();
  values.serviceType = item.svc;
  values.quantity = parseNumber(item.qtd);
  values.daily = parseNumber(item.dias) || 1;
  values.taxData.tariff = {
    ...values.taxData.tariff,
    typeTax: "DAILY",
    value: parseNumber(item.valor),
  };

  [...SHARED_TAX_KEYS, ...ITEM_TAX_KEYS].forEach((key) => {
    const field = TAX_FIELDS[key];
    values.taxData[field] = toBudgetTax(
      values.taxData[field] as DefaultTax,
      effectiveTax(draft, item, key)
    ) as never;
  });

  const markup = effectiveTax(draft, item, "mk");
  const commission = effectiveTax(draft, item, "com");
  const supplierTribute = effectiveTax(draft, item, "imk");
  values.appliedValues = {
    markupOverride: hasValue(markup),
    markupOverrideValue: {
      calcType: markup?.mode === "R$" ? "FIX_VALUE" : "PERCENTAGE",
      value: markup?.v ? parseNumber(markup.v) : 0,
    },
    commissionOverride: hasValue(commission),
    commissionOverrideValue: {
      calcType: commission?.mode === "R$" ? "FIX_VALUE" : "PERCENTAGE",
      value: commission?.v ? parseNumber(commission.v) : 0,
    },
    supplierTributeOverride: hasValue(supplierTribute),
    supplierTributeOverrideValue: {
      calcType: supplierTribute?.mode === "R$" ? "FIX_VALUE" : "PERCENTAGE",
      value: supplierTribute?.v ? parseNumber(supplierTribute.v) : 0,
    },
  };

  return calculateServiceTotals(
    values,
    item.forma,
    Math.max(numberOfServices(draft, item), 1)
  );
}
